import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, XIcon, SmartphoneIcon, ShieldCheckIcon, GlobeIcon, CoffeeIcon, FlameIcon, TreesIcon, LeafIcon, ZapIcon, CheckCircle2Icon, ArrowUpRightIcon, BarChart3Icon, UsersIcon, MapPinIcon, FileTextIcon } from 'lucide-react';
interface PresentationProps {
  onExit: () => void;
}
const TOTAL_SLIDES = 9;
/* ─── Decorative SVG Patterns ─── */
function GridPattern({
  id,
  opacity = 0.04
}: {
  id: string;
  opacity?: number;
}) {
  return <svg className="absolute inset-0 w-full h-full" style={{
    opacity
  }} aria-hidden="true" data-id="element-1790">
      <defs data-id="element-1791">
        <pattern id={id} width="60" height="60" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1792">
          <line x1="0" y1="0" x2="0" y2="60" stroke="currentColor" strokeWidth="0.5" data-id="element-1793" />
          <line x1="0" y1="0" x2="60" y2="0" stroke="currentColor" strokeWidth="0.5" data-id="element-1794" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} data-id="element-1795" />
    </svg>;
}
function HexagonDecoration({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 200 200" className={className} aria-hidden="true" data-id="element-1796">
      <polygon points="100,10 180,55 180,145 100,190 20,145 20,55" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15" data-id="element-1797" />
      <polygon points="100,35 160,70 160,130 100,165 40,130 40,70" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.1" data-id="element-1798" />
      <polygon points="100,60 140,85 140,115 100,140 60,115 60,85" fill="currentColor" fillOpacity="0.05" stroke="currentColor" strokeWidth="1.5" opacity="0.2" data-id="element-1799" />
      <circle cx="100" cy="10" r="3" fill="currentColor" opacity="0.3" data-id="element-1800" />
      <circle cx="180" cy="55" r="2.5" fill="currentColor" opacity="0.2" data-id="element-1801" />
      <circle cx="180" cy="145" r="3" fill="currentColor" opacity="0.3" data-id="element-1802" />
      <circle cx="100" cy="190" r="2.5" fill="currentColor" opacity="0.2" data-id="element-1803" />
      <circle cx="20" cy="145" r="3" fill="currentColor" opacity="0.3" data-id="element-1804" />
      <circle cx="20" cy="55" r="2.5" fill="currentColor" opacity="0.2" data-id="element-1805" />
    </svg>;
}
function CircuitDecoration({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 300 300" className={className} aria-hidden="true" data-id="element-1806">
      <circle cx="150" cy="150" r="120" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.08" data-id="element-1807" />
      <circle cx="150" cy="150" r="80" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.06" data-id="element-1808" />
      <circle cx="150" cy="150" r="40" fill="currentColor" fillOpacity="0.03" stroke="currentColor" strokeWidth="1" opacity="0.1" data-id="element-1809" />
      <line x1="150" y1="30" x2="150" y2="70" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-1810" />
      <line x1="270" y1="150" x2="230" y2="150" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-1811" />
      <line x1="150" y1="270" x2="150" y2="230" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-1812" />
      <line x1="30" y1="150" x2="70" y2="150" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-1813" />
      <circle cx="150" cy="30" r="4" fill="currentColor" opacity="0.15" data-id="element-1814" />
      <circle cx="270" cy="150" r="4" fill="currentColor" opacity="0.15" data-id="element-1815" />
      <circle cx="150" cy="270" r="4" fill="currentColor" opacity="0.15" data-id="element-1816" />
      <circle cx="30" cy="150" r="4" fill="currentColor" opacity="0.15" data-id="element-1817" />
    </svg>;
}
/* ─── Slide Components ─── */
function SlideWrapper({
  children,
  bg,
  textColor = 'text-carbon-deep',
  dark = false,
  hideWatermark = false
}: {
  children: React.ReactNode;
  bg: string;
  textColor?: string;
  dark?: boolean;
  hideWatermark?: boolean;
}) {
  return <div className={`relative w-full h-full flex items-center justify-center overflow-hidden ${bg} ${textColor}`} data-id="element-1818">
      {children}
      {/* Logo watermark — bottom right of every slide (unless hidden) */}
      {!hideWatermark && <img src="https://cdn.magicpatterns.com/uploads/jGYrssm7tbfxt25FxeAxXx/CARBONSAFELOGO.png" alt="" aria-hidden="true" className="absolute bottom-6 right-6 h-10 w-auto pointer-events-none select-none z-20" style={{
      opacity: 1
    }} data-id="element-1819" />}
    </div>;
}
/* Slide 1: Title */
function SlideTitleContent() {
  return <SlideWrapper bg="bg-[#FAFDF7]" dark={false} hideWatermark data-id="element-1820">
      <GridPattern id="title-grid" opacity={0.03} data-id="element-1821" />
      <HexagonDecoration className="absolute -right-16 -top-16 w-80 h-80 text-carbon-bright" data-id="element-1822" />
      <HexagonDecoration className="absolute -left-20 -bottom-20 w-96 h-96 text-carbon-bright" data-id="element-1823" />

      <div className="relative z-10 text-center max-w-4xl px-8" data-id="element-1824">
        <motion.img src="https://cdn.magicpatterns.com/uploads/jGYrssm7tbfxt25FxeAxXx/CARBONSAFELOGO.png" alt="CarbonSafe" className="h-14 sm:h-16 mx-auto mb-10" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1825" />
        <motion.h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-carbon-deep tracking-tight leading-[1.08]" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1826">
          Verified supply chains
          <br data-id="element-1827" />
          <span className="text-carbon-bright" data-id="element-1828">from Africa's farms</span>
          <br data-id="element-1829" />
          to global markets
        </motion.h1>
        <motion.p className="mt-8 text-lg sm:text-xl text-carbon-mid max-w-2xl mx-auto leading-relaxed" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1830">
          Independent dMRV-as-a-Service for Africa–EU trade and finance
          compliance
        </motion.p>
        <motion.div className="mt-8 flex flex-wrap items-center justify-center gap-3" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} data-id="element-1831">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1832">
            <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1833" />
            EUDR Compliant
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1834">
            <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1835" />
            Verra VCS
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1836">
            <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1837" />
            Gold Standard
          </span>
        </motion.div>
      </div>
    </SlideWrapper>;
}
/* Slide 2: The Problem */
function SlideProblemContent() {
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-1838">
      <GridPattern id="problem-grid" opacity={0.03} data-id="element-1839" />
      {/* Background photo overlay */}
      <div className="absolute inset-0 z-0" data-id="element-1840">
        <img src="https://images.unsplash.com/photo-1589923188651-268a9765e432?w=1600&h=900&fit=crop&crop=center" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-1841" />
        <div className="absolute inset-0 bg-carbon-deep/90" data-id="element-1842" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-8" data-id="element-1843">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1844">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1845">
            <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-1846">
              The Problem
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]" data-id="element-1847">
              Africa's supply chains are
              <span className="text-carbon-bright" data-id="element-1848"> invisible</span> to global
              markets
            </h2>
            <p className="mt-6 text-lg text-white/60 leading-relaxed" data-id="element-1849">
              1.7 million Ugandan smallholders produce $1.3B+ in exports
              annually — but without verifiable data, they can't access premium
              markets, carbon finance, or EU trade compliance.
            </p>
          </motion.div>

          <motion.div className="space-y-5" initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1850">
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-1851">
              <p className="text-3xl font-extrabold text-red-400" data-id="element-1852">$900M+</p>
              <p className="mt-1 text-sm text-white/50" data-id="element-1853">
                Uganda's coffee exports to the EU — at risk without EUDR
                compliance
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-1854">
              <p className="text-3xl font-extrabold text-amber-400" data-id="element-1855">95%</p>
              <p className="mt-1 text-sm text-white/50" data-id="element-1856">
                Of households use biomass cooking — unverified emission
                reductions
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-1857">
              <p className="text-3xl font-extrabold text-carbon-bright" data-id="element-1858">
                90K ha/yr
              </p>
              <p className="mt-1 text-sm text-white/50" data-id="element-1859">
                Forest lost annually — REDD+ credits require rigorous dMRV
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 3: The Solution */
function SlideSolutionContent() {
  return <SlideWrapper bg="bg-[#FAFDF7]" dark={false} data-id="element-1860">
      <GridPattern id="solution-grid" opacity={0.03} data-id="element-1861" />
      <HexagonDecoration className="absolute -left-12 top-12 w-64 h-64 text-carbon-bright" data-id="element-1862" />

      <div className="relative z-10 max-w-5xl mx-auto px-8" data-id="element-1863">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1864">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1865">
            <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-1866">
              The Solution
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1867">
              A mobile-first
              <span className="text-carbon-bright" data-id="element-1868"> dMRV platform</span>
            </h2>
            <p className="mt-6 text-lg text-carbon-mid leading-relaxed" data-id="element-1869">
              Farmers use USSD or our mobile app to log harvest data. CarbonSafe
              generates Digital Quality Certificates that international buyers
              and carbon registries trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-1870">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-carbon-bright/5 border border-carbon-geo" data-id="element-1871">
                <SmartphoneIcon className="w-5 h-5 text-carbon-bright" data-id="element-1872" />
                <span className="text-sm font-semibold text-carbon-deep" data-id="element-1873">
                  USSD + App
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-carbon-bright/5 border border-carbon-geo" data-id="element-1874">
                <MapPinIcon className="w-5 h-5 text-carbon-bright" data-id="element-1875" />
                <span className="text-sm font-semibold text-carbon-deep" data-id="element-1876">
                  GPS-Tagged
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-carbon-bright/5 border border-carbon-geo" data-id="element-1877">
                <ShieldCheckIcon className="w-5 h-5 text-carbon-bright" data-id="element-1878" />
                <span className="text-sm font-semibold text-carbon-deep" data-id="element-1879">
                  Tamper-Proof
                </span>
              </div>
            </div>
          </motion.div>

          {/* Farmer with phone photo + certificate overlay */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative" data-id="element-1880">
            <div className="overflow-hidden shadow-xl" data-id="element-1881">
              <img src="https://static.vecteezy.com/system/resources/previews/053/560/533/large_2x/female-african-farmer-using-her-phone-photo.jpg" alt="African farmer using mobile phone in field" className="w-full h-[340px] object-cover" data-id="element-1882" />
            </div>
            {/* Floating certificate badge */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl border border-carbon-geo shadow-lg p-3 max-w-[200px]" data-id="element-1883">
              <div className="flex items-center gap-2 mb-2" data-id="element-1884">
                <div className="w-6 h-6 rounded-md bg-carbon-bright flex items-center justify-center" data-id="element-1885">
                  <span className="text-white text-[8px] font-bold" data-id="element-1886">CS</span>
                </div>
                <span className="text-[10px] font-bold text-carbon-deep" data-id="element-1887">
                  Digital Certificate
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1888">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-carbon-bright" data-id="element-1889" />
                <span className="text-[10px] font-semibold text-carbon-bright" data-id="element-1890">
                  Verified & Compliant
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 4: How It Works */
function SlideHowItWorksContent() {
  const steps = [{
    icon: SmartphoneIcon,
    num: '01',
    title: 'Capture',
    desc: 'Farmers log data via USSD or mobile app — GPS-tagged, timestamped, tamper-proof.'
  }, {
    icon: BarChart3Icon,
    num: '02',
    title: 'Verify',
    desc: 'AI models cross-reference satellite imagery, field data, and sensor readings.'
  }, {
    icon: FileTextIcon,
    num: '03',
    title: 'Certify',
    desc: 'Digital Quality Certificates issued with full compliance documentation.'
  }, {
    icon: GlobeIcon,
    num: '04',
    title: 'Trade',
    desc: 'Certificates unlock EU market access, carbon credits, and trade finance.'
  }];
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-1891">
      <GridPattern id="how-grid" opacity={0.03} data-id="element-1892" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-1893">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1894">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-1895">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-id="element-1896">
            Four steps to{' '}
            <span className="text-carbon-bright" data-id="element-1897">verified impact</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1898">
          {steps.map((step, i) => <motion.div key={step.num} className="relative p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15 + i * 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1899">
              <span className="text-[10px] font-bold text-carbon-bright/50 uppercase tracking-widest" data-id="element-1900">
                {step.num}
              </span>
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/15 flex items-center justify-center mt-3 mb-4" data-id="element-1901">
                <step.icon className="w-6 h-6 text-carbon-bright" data-id="element-1902" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1903">
                {step.title}
              </h3>
              <p className="text-sm text-white/50 leading-relaxed" data-id="element-1904">
                {step.desc}
              </p>
              {i < 3 && <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-20" data-id="element-1905">
                  <ArrowRightIcon className="w-5 h-5 text-carbon-bright/30" data-id="element-1906" />
                </div>}
            </motion.div>)}
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 5: Verticals */
function SlideVerticalsContent() {
  const verticals = [{
    icon: CoffeeIcon,
    title: 'Coffee Traceability',
    desc: 'Farm-to-cup EUDR compliance',
    color: 'bg-amber-500/10 text-amber-600',
    image: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=300&h=200&fit=crop&crop=center'
  }, {
    icon: FlameIcon,
    title: 'Cookstove Credits',
    desc: 'IoT-verified emission reductions',
    color: 'bg-orange-500/10 text-orange-600',
    image: 'https://www.engineeringforchange.org/wp-content/uploads/2018/02/ace1stove-1_resized.jpg'
  }, {
    icon: TreesIcon,
    title: 'Forestry & REDD+',
    desc: 'Satellite-verified forest carbon',
    color: 'bg-emerald-500/10 text-emerald-600',
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=300&h=200&fit=crop&crop=center'
  }, {
    icon: LeafIcon,
    title: 'Agroforestry',
    desc: 'Soil carbon sequestration',
    color: 'bg-green-500/10 text-green-600',
    image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=300&h=200&fit=crop&crop=center'
  }, {
    icon: ZapIcon,
    title: 'Renewable Energy',
    desc: 'Clean energy carbon credits',
    color: 'bg-blue-500/10 text-blue-600',
    image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=300&h=200&fit=crop&crop=center'
  }];
  return <SlideWrapper bg="bg-[#FAFDF7]" dark={false} data-id="element-1907">
      <GridPattern id="vert-grid" opacity={0.03} data-id="element-1908" />
      <HexagonDecoration className="absolute -right-16 -bottom-16 w-72 h-72 text-carbon-bright" data-id="element-1909" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-1910">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1911">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-1912">
            dMRV Verticals
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1913">
            Five sectors,{' '}
            <span className="text-carbon-bright" data-id="element-1914">one platform</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-1915">
          {verticals.map((v, i) => <motion.div key={v.title} className="border border-carbon-geo bg-white overflow-hidden text-center" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1 + i * 0.08,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1916">
              <img src={v.image} alt={v.title} className="w-full h-24 object-cover" data-id="element-1917" />
              <div className="p-4" data-id="element-1918">
                <div className={`w-10 h-10 rounded-xl ${v.color} flex items-center justify-center mx-auto mb-3 -mt-8 relative z-10 border-2 border-white shadow-sm`} data-id="element-1919">
                  <v.icon className="w-5 h-5" data-id="element-1920" />
                </div>
                <h3 className="text-sm font-bold text-carbon-deep mb-1" data-id="element-1921">
                  {v.title}
                </h3>
                <p className="text-xs text-carbon-mid leading-snug" data-id="element-1922">{v.desc}</p>
              </div>
            </motion.div>)}
        </div>

        <motion.p className="text-center mt-8 text-base text-carbon-mid" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} data-id="element-1923">
          Each vertical uses the same core platform — adapted with
          sector-specific methodologies and compliance frameworks.
        </motion.p>
      </div>
    </SlideWrapper>;
}
/* Slide 6: Market Opportunity */
function SlideMarketContent() {
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-1924">
      <GridPattern id="market-grid" opacity={0.03} data-id="element-1925" />
      {/* Background photo */}
      <div className="absolute inset-0 z-0" data-id="element-1926">
        <img src="https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?w=1600&h=900&fit=crop&crop=center" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-1927" />
        <div className="absolute inset-0 bg-carbon-deep/92" data-id="element-1928" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-1929">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1930">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-1931">
            Market Opportunity
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-id="element-1932">
            The numbers speak for{' '}
            <span className="text-carbon-bright" data-id="element-1933">themselves</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1934">
          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1935">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1936">
              1.7M+
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-1937">
              Smallholder Farmers
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1938">
              Across Uganda's growing regions
            </p>
          </motion.div>

          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1939">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1940">
              $1.3B+
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-1941">
              Annual Exports
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1942">
              Uganda's agricultural export value
            </p>
          </motion.div>

          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1943">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1944">
              $735M+
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-1945">
              Carbon Credit Potential
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1946">
              Across all five verticals annually
            </p>
          </motion.div>

          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1947">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1948">
              Dec '25
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-1949">
              EUDR Deadline
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1950">EU enforcement begins</p>
          </motion.div>
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 7: Platform */
function SlidePlatformContent() {
  return <SlideWrapper bg="bg-[#FAFDF7]" dark={false} data-id="element-1951">
      <GridPattern id="platform-grid" opacity={0.03} data-id="element-1952" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-1953">
        <motion.div className="text-center mb-10" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1954">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-1955">
            The Platform
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1956">
            One dashboard for{' '}
            <span className="text-carbon-bright" data-id="element-1957">everything</span>
          </h2>
        </motion.div>

        <motion.div className="rounded-2xl shadow-2xl border border-carbon-geo overflow-hidden bg-white" style={{
        transform: 'perspective(1200px) rotateX(2deg) scale(0.92)'
      }} initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1958">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200" data-id="element-1959">
            <div className="flex gap-1.5" data-id="element-1960">
              <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-1961" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-1962" />
              <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-1963" />
            </div>
            <div className="flex-1 mx-4" data-id="element-1964">
              <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono border border-gray-200 max-w-md" data-id="element-1965">
                app.carbonsafe.io/dashboard
              </div>
            </div>
          </div>
          <div className="flex min-h-[280px] sm:min-h-[320px]" data-id="element-1966">
            <div className="w-40 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-1967">
              <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-1968">
                <div className="w-6 h-6 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-1969">
                  <span className="text-white text-[9px] font-bold" data-id="element-1970">CS</span>
                </div>
                <span className="text-white text-xs font-bold" data-id="element-1971">CarbonSafe</span>
              </div>
              <nav className="space-y-0.5 text-[11px]" data-id="element-1972">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/10 text-white font-medium" data-id="element-1973">
                  <BarChart3Icon className="w-3 h-3" data-id="element-1974" /> Overview
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-1975">
                  <CoffeeIcon className="w-3 h-3" data-id="element-1976" /> Coffee
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-1977">
                  <FlameIcon className="w-3 h-3" data-id="element-1978" /> Cookstoves
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-1979">
                  <TreesIcon className="w-3 h-3" data-id="element-1980" /> Forestry
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-1981">
                  <LeafIcon className="w-3 h-3" data-id="element-1982" /> Agroforestry
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-1983">
                  <ZapIcon className="w-3 h-3" data-id="element-1984" /> Energy
                </div>
              </nav>
            </div>
            <div className="flex-1 p-4 bg-gray-50" data-id="element-1985">
              <div className="flex items-center justify-between mb-3" data-id="element-1986">
                <div data-id="element-1987">
                  <h3 className="text-xs font-bold text-carbon-deep" data-id="element-1988">
                    All Verticals Overview
                  </h3>
                  <p className="text-[10px] text-gray-400" data-id="element-1989">
                    Last synced 3 min ago
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold" data-id="element-1990">
                  <span className="w-1 h-1 rounded-full bg-emerald-500" data-id="element-1991" /> All
                  Systems Online
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-3" data-id="element-1992">
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-1993">
                  <p className="text-[9px] text-gray-400" data-id="element-1994">Farms Registered</p>
                  <p className="text-base font-bold text-carbon-deep" data-id="element-1995">17,127</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-1996">
                  <p className="text-[9px] text-gray-400" data-id="element-1997">Certificates</p>
                  <p className="text-base font-bold text-carbon-bright" data-id="element-1998">
                    4,892
                  </p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-1999">
                  <p className="text-[9px] text-gray-400" data-id="element-2000">tCO₂e Verified</p>
                  <p className="text-base font-bold text-carbon-deep" data-id="element-2001">28,400</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2002">
                  <p className="text-[9px] text-gray-400" data-id="element-2003">Credit Value</p>
                  <p className="text-base font-bold text-carbon-deep" data-id="element-2004">$568K</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2005">
                  <p className="text-[9px] text-gray-400" data-id="element-2006">Compliance</p>
                  <p className="text-base font-bold text-carbon-bright" data-id="element-2007">
                    98.7%
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2008">
                <p className="text-[10px] font-semibold text-carbon-deep mb-2" data-id="element-2009">
                  Monthly Verified Impact
                </p>
                <div className="flex items-end gap-1.5 h-16" data-id="element-2010">
                  {[35, 48, 42, 65, 58, 72, 55, 80, 68, 85, 78, 92].map((h, i) => <div key={i} className="flex-1" data-id="element-2011">
                        <div className="w-full rounded-t-sm" style={{
                    height: `${h}%`,
                    backgroundColor: i >= 9 ? '#52B788' : i >= 6 ? '#6BCB77' : '#B7DFCA'
                  }} data-id="element-2012" />
                      </div>)}
                </div>
                <div className="flex justify-between mt-1" data-id="element-2013">
                  <span className="text-[8px] text-gray-400" data-id="element-2014">Jan</span>
                  <span className="text-[8px] text-gray-400" data-id="element-2015">Jun</span>
                  <span className="text-[8px] text-gray-400" data-id="element-2016">Dec</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </SlideWrapper>;
}
/* Slide 8: Trust & Compliance */
function SlideTrustContent() {
  const standards = [{
    name: 'EUDR',
    desc: 'EU Deforestation Regulation compliance for all agricultural exports',
    color: 'bg-blue-500'
  }, {
    name: 'Verra VCS',
    desc: 'Verified Carbon Standard for forestry, cookstove, and soil carbon credits',
    color: 'bg-emerald-500'
  }, {
    name: 'Gold Standard',
    desc: 'Premium carbon credits with SDG co-benefit verification',
    color: 'bg-amber-500'
  }, {
    name: 'UCDA',
    desc: 'Uganda Coffee Development Authority quality certification',
    color: 'bg-green-600'
  }, {
    name: 'ISO 14064',
    desc: 'International standard for greenhouse gas accounting and verification',
    color: 'bg-purple-500'
  }, {
    name: 'UNFCCC CDM',
    desc: 'Clean Development Mechanism methodology compliance',
    color: 'bg-sky-500'
  }];
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-2017">
      <GridPattern id="trust-grid" opacity={0.03} data-id="element-2018" />
      <HexagonDecoration className="absolute -right-20 -top-20 w-80 h-80 text-carbon-bright" data-id="element-2019" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-2020">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2021">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-2022">
            Trust & Compliance
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-id="element-2023">
            Built on{' '}
            <span className="text-carbon-bright" data-id="element-2024">global standards</span>
          </h2>
          <p className="mt-4 text-lg text-white/50 max-w-2xl mx-auto" data-id="element-2025">
            Every certificate and credit is independently verified against the
            world's most rigorous frameworks.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2026">
          {standards.map((s, i) => <motion.div key={s.name} className="flex items-start gap-4 p-5 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1 + i * 0.07,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2027">
              <div className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center flex-shrink-0`} data-id="element-2028">
                <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-2029" />
              </div>
              <div data-id="element-2030">
                <h3 className="text-sm font-bold text-white" data-id="element-2031">{s.name}</h3>
                <p className="text-xs text-white/40 mt-1 leading-snug" data-id="element-2032">
                  {s.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 9: CTA */
function SlideCTAContent() {
  return <SlideWrapper bg="bg-[#FAFDF7]" dark={false} hideWatermark data-id="element-2033">
      <GridPattern id="cta-grid" opacity={0.03} data-id="element-2034" />
      <HexagonDecoration className="absolute -left-16 -top-16 w-72 h-72 text-carbon-bright" data-id="element-2035" />
      <HexagonDecoration className="absolute -right-12 -bottom-12 w-64 h-64 text-carbon-bright" data-id="element-2036" />
      <CircuitDecoration className="absolute right-1/4 top-1/4 w-[300px] h-[300px] text-carbon-bright" data-id="element-2037" />

      <div className="relative z-10 text-center max-w-3xl mx-auto px-8" data-id="element-2038">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2039">
          <img src="https://cdn.magicpatterns.com/uploads/jGYrssm7tbfxt25FxeAxXx/CARBONSAFELOGO.png" alt="CarbonSafe" className="h-12 mx-auto mb-8" data-id="element-2040" />
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-2041">
            Every shipment tells a story.
            <br data-id="element-2042" />
            <span className="text-carbon-bright" data-id="element-2043">Make yours verified.</span>
          </h2>
          <p className="mt-6 text-lg text-carbon-mid max-w-xl mx-auto leading-relaxed" data-id="element-2044">
            Join producers, exporters, and project developers using CarbonSafe
            to unlock verified trade and carbon finance across Africa.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2045">
            <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2.5 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-2046">
              Book a Demo
              <ArrowUpRightIcon className="w-4 h-4" data-id="element-2047" />
            </a>
            <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2.5 px-8 py-4 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-2048">
              Download Deck (PDF)
            </a>
          </div>
          <p className="mt-10 text-sm text-carbon-mid/60" data-id="element-2049">
            hello@carbonsafe.earth · carbonsafe.earth
          </p>
        </motion.div>
      </div>
    </SlideWrapper>;
}
/* ─── Slide Registry ─── */
const slides = [SlideTitleContent, SlideProblemContent, SlideSolutionContent, SlideHowItWorksContent, SlideVerticalsContent, SlideMarketContent, SlidePlatformContent, SlideTrustContent, SlideCTAContent];
/* ─── Main Presentation Component ─── */
export function Presentation({
  onExit
}: PresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= TOTAL_SLIDES || index === currentSlide) return;
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);
  const next = useCallback(() => goTo(currentSlide + 1), [currentSlide, goTo]);
  const prev = useCallback(() => goTo(currentSlide - 1), [currentSlide, goTo]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        onExit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, prev, onExit]);
  const SlideContent = slides[currentSlide];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  return <div className="fixed inset-0 z-[100] bg-black" data-id="element-2050">
      {/* Slide Area */}
      <div className="relative w-full h-full overflow-hidden" data-id="element-2051">
        <AnimatePresence mode="wait" custom={direction} data-id="element-2052">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute inset-0" data-id="element-2053">
            <SlideContent data-id="element-2054" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Top bar: exit + slide counter */}
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4" data-id="element-2055">
        <button onClick={onExit} className="w-9 h-9 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/40 transition-colors" aria-label="Exit presentation" data-id="element-2056">
          <XIcon className="w-4 h-4 text-white" data-id="element-2057" />
        </button>
        <span className="text-xs font-bold text-white/40 tabular-nums tracking-wider bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10" data-id="element-2058">
          {currentSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>

      {/* Bottom navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4" data-id="element-2059">
        {/* Nav arrows */}
        <div className="flex items-center gap-2" data-id="element-2060">
          <button onClick={prev} disabled={currentSlide === 0} className="w-10 h-10 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Previous slide" data-id="element-2061">
            <ArrowLeftIcon className="w-4 h-4 text-white" data-id="element-2062" />
          </button>
          <button onClick={next} disabled={currentSlide === TOTAL_SLIDES - 1} className="w-10 h-10 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next slide" data-id="element-2063">
            <ArrowRightIcon className="w-4 h-4 text-white" data-id="element-2064" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center gap-1.5" data-id="element-2065">
          {slides.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`rounded-full transition-all duration-300 ${i === currentSlide ? 'w-8 h-2 bg-carbon-bright' : i < currentSlide ? 'w-2 h-2 bg-carbon-bright/40' : 'w-2 h-2 bg-white/20 hover:bg-white/30'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-2066" />)}
        </div>

        {/* Keyboard hint */}
        <span className="text-[10px] text-white/30 font-medium tracking-wider uppercase hidden sm:block" data-id="element-2067">
          ← → to navigate · Esc to exit
        </span>
      </div>
    </div>;
}